import jwtDecode from "jwt-decode";
import { Outlet, Navigate, history } from "./AppRouter";
import store from "../store/store";
import { setSearch } from "../store/actions/setSearch";
import { FC, useState, useEffect, useMemo, useContext } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  authenticated,
  tryAutoLogin,
  getPayload,
  logout,
  updateAbility,
} from "../utils/auth";
import { AbilityContext } from "../components/shared/AbilityContext";
import { GET_USER_PRIVACY, UPDATE_USER_EXTRAS } from "../store/new-queries/users";
import PrivacyPolicyModal from "../components/pages/PrivacyPolicyModal";

const setFilters = () => {
  if (history.location.search !== "") {
    const token = jwtDecode(history.location.search);
    store.dispatch(setSearch({ type: "setPaginationControl", payload: token }));
  } else {
    store.dispatch(setSearch({ type: "totalReset" }));
  }
};

interface Props {
  redirectPath: string;
  children: any;
}

const ProtectedRoute: FC<Props> = ({ redirectPath = "/login", children }) => {
  const ability = useContext(AbilityContext);
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const userId = useMemo(() => {
    try {
      return getPayload()?.id ?? null;
    } catch {
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const [getPrivacy, { data: privacyData, loading: privacyLoading, called }] = useLazyQuery(GET_USER_PRIVACY, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (isAuthenticated && userId && !privacyAccepted) {
      getPrivacy({ variables: { id: userId } });
    }
  }, [isAuthenticated, userId, privacyAccepted, getPrivacy]);

  const [updateUserExtras, { loading: updatingExtras }] = useMutation(UPDATE_USER_EXTRAS);

  const privacyUser = privacyData?.users?.[0];
  const privacyNotAccepted =
    !!privacyUser && !privacyUser?.extras?.privacy_policy && !privacyAccepted;

  const handleAccept = async () => {
    if (!privacyUser?.id) return;
    await updateUserExtras({
      variables: {
        id: privacyUser.id,
        data: { extras: { ...(privacyUser.extras ?? {}), privacy_policy: true } },
      },
    });
    setPrivacyAccepted(true);
  };

  useEffect(() => {
    const checkAuth = async () => {
      if (authenticated()) {
        setIsAuthenticated(true);
        setIsChecking(false);
        return;
      }

      const autoLoginSuccess = await tryAutoLogin();

      if (autoLoginSuccess && authenticated()) {
        updateAbility(ability);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }

      setIsChecking(false);
    };

    checkAuth();
  }, []);

  if (isChecking) {
    // Mostrar un loader mientras se verifica la autenticación
    return null; // O puedes retornar un componente de loading
  }

  if (isAuthenticated) {
    try {
      setFilters();
    } catch (e) {
      console.error(e);
    }

    if (!called || privacyLoading) return null;

    if (privacyNotAccepted) {
      return (
        <PrivacyPolicyModal
          visible={true}
          loading={updatingExtras}
          onAccept={handleAccept}
          onDeny={logout}
        />
      );
    }

    return children ? children : <Outlet />;
  }
  
  // Guardar la URL actual para redirigir después del login
  const currentPath = history.location.pathname + history.location.search;
  const loginPath = redirectPath.includes('?') 
    ? `${redirectPath}&redirect=${encodeURIComponent(currentPath)}`
    : `${redirectPath}?redirect=${encodeURIComponent(currentPath)}`;
  
  return <Navigate to={loginPath} replace />;
};

export default ProtectedRoute;
